<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col-12">
        <div class="page-table-header">
          <PageHeader :page-title="'Invoices'">
            <template v-slot:buttons>
              <b-button
                class="button title-container__controls-item"
                :variant="isShowBulkCreation ? 'secondary' : 'info'"
                @click="isShowBulkCreation = !isShowBulkCreation"
              >
                <span class="material-icons icon-image-preview">
                  tune
                </span>
                Bulk Creation
              </b-button>
              <b-button
                class="button title-container__controls-item"
                :variant="isShowFilter ? 'secondary' : 'info'"
                @click="toggleShowFilter"
              >
                <span class="material-icons icon-image-preview">
                  tune
                </span>
                Filters
              </b-button>
              <b-button
                variant="success"
                v-if="$can(permissions.CreateFinancesInvoices)"
                type="button"
                class="button"
                @click.prevent="showCreateInvoices"
                :disabled="isDataPageLoading"
              >
                Create
              </b-button>
            </template>
          </PageHeader>

          <Filters
            v-show="isShowFilter"
            textButtonSubmit="Filter"
            :data="filters"
            :isLoading="isDataPageLoading"
            @submit="setFilters"
          />

          <InvoicesBulkCreation
            v-show="isShowBulkCreation"
            @updateInvoices="updateInvoices()"
          />

          <div v-if="getClientsBankDetailsTableData.length">
            <TableForPage
              ref="table"
              :tableData="getClientsBankDetailsTableData"
              :fields="table.fields"
              :perPage="getInvoices.per_page"
              :currentPage="getInvoices.current_page"
              :totalRows="getInvoices.total"
              :slots="table.slots"
              :tableRowModalFields="tableRowModal.fields"
              :actions="table.actions"
              isServerRender
              noLocalSorting
              showTotalTable
              @remove="removeInvoices"
              @update="updateInvoices"
            >
              <template v-slot:rowModal.client_name="slotProps">
                <td class="entities-table-row-modal__label">Client</td>
                <td>
                  {{ getFullName(slotProps.field)}}
                </td>
              </template>
              <template v-slot:rowModal.periods="slotProps">
                <template v-if="slotProps.field.length">
                  <td class="entities-table-row-modal__label">Periods</td>
                  <td>
                    <div>
                      <div class="entities-table-row-modal__period" v-for="(period, index) in slotProps.field" :key="index">
                        <div>
                          Date from: {{ period.date_from }}
                        </div>
                        <div>
                          Date to: {{ period.date_to }}
                        </div>
                        <div>
                          Hours amount: {{ period.hours_amount }}
                        </div>
                        <div>
                          Total amount: {{ period.total_amount }}
                        </div>
                        <div>
                          Invoice period type: {{ period.invoice_period_type.name }}
                        </div>
                        <div>
                          Period cost EUR: {{ period.period_costEUR }}
                        </div>
                        <div>
                          Period cost UAH: {{ period.period_costUAH }}
                        </div>
                        <div>
                          Period cost USD: {{ period.period_costUSD }}
                        </div>
                      </div>
                    </div>
                  </td>
                </template>
              </template>
              <template v-slot:rowModal.items="slotProps">
                <template v-if="slotProps.field.length">
                  <td class="entities-table-row-modal__label">Items</td>
                  <td>
                    <div>
                      <div class="entities-table-row-modal__items" v-for="(period, index) in slotProps.field" :key="index">
                        <div>
                          Name: {{ period.type_display_name }}
                        </div>
                        <div>
                          Hour Cost: {{ period.pivot.hour_cost }}
                        </div>
                        <div>
                          Hours Amount: {{ period.pivot.hours_amount }}
                        </div>
                      </div>
                    </div>
                  </td>
                </template>
              </template>
              <template v-slot:invoice_number="slotProps">
                <a href="#" @click.prevent="showViewModal(slotProps)">{{ slotProps.item.invoice_number }}</a>
              </template>
              <template v-slot:own_bank_details="slotProps">
                <LabelBankCard :card_details="slotProps.item.own_bank_details.summary" />
              </template>
              <template v-slot:client_bank_details="slotProps">
                <LabelBankCard :card_details="slotProps.item.client_bank_details.summary" />
              </template>
              <template v-slot:invoice_type="slotProps">
                <LabelInvoiceType :type="slotProps.item.invoice_type" />
              </template>
              <template v-slot:invoice_status="slotProps">
                <LabelTableStatus :status="slotProps.item.invoice_status" />
              </template>
              <template v-slot:total_amount="slotProps">
                {{ slotProps.item.total_amount }} {{getCurrencyCode(slotProps.item)}}
              </template>
              <template v-slot:files="slotProps">
                <InvoicesLinks
                  :templates="getTemplates"
                  :item="slotProps.item"
                  @reloadInvoices="updateInvoices"
                />
              </template>
            </TableForPage>

            <div>
              <div class="mt-3">
                <b-card-group deck class="mb-3">
                  <b-card border-variant="dark" class="text-center">
                    <b-card-text>
                      <h3>Original totals</h3>
                      <span v-for="(amount, currencyCode) in totalAmount.original" :key="currencyCode">
                          {{ amount }}
                          <b-badge variant="light"> {{currencyCode}} </b-badge>
                      </span>
                    </b-card-text>
                  </b-card>

                  <b-card border-variant="dark" align="center">
                    <b-card-text>
                      <h3>Equivalent totals</h3>
                      <span v-for="(amount, currencyCode) in totalAmount.equivalent" :key="currencyCode">
                          {{ amount }}
                          <b-badge variant="light"> {{currencyCode}} </b-badge>
                      </span>
                    </b-card-text>
                  </b-card>

                </b-card-group>
              </div>
            </div>
          </div>

          <div class="table__empty" v-else>
            Table is empty
          </div>
        </div>
      </div>
    </div>

    <FinanceInvoicesModal
      v-model="createInvoices.show"
      mode="create"
      :data="createInvoices.form"
      :title="createInvoices.title"
      :submitButtonText="createInvoices.submitButtonText"
      :bankDetails="getOwnBankDetails"
      :clientBankDetails="getClientsBankDetailsAll"
      :templates="getTemplates"
      :projects="getActualProjects"
      :statuses="getStatuses"
      :types="getInvoicesTypes"
      :isLoading="getIsModalLoading"
      @submit="createFinanceInvoices"
    />
  </div>
</template>

<script>
import Moment from 'moment'
import { permissions } from '@mocks/permissions'
import TableForPage from '@tables/TableForPage'
import FinanceInvoicesModal from '@components/modals/FinanceInvoicesModal'
import Filters from '@components/Filter/Filter'
import LabelBankCard from '@components/tables/elements/LabelBankCard'
import LabelTableStatus from '@components/tables/elements/LabelTableStatus'
import LabelInvoiceType from '@components/tables/elements/LabelInvoiceType'
import PageHeader from '@components/PageHeader'
import InvoiceFormModal from '../mixins/InvoiceFormModal'
import InvoicesLinks from '../components/InvoicesLinks'
import InvoicesBulkCreation from '../components/InvoicesBulkCreation'

export default {
  name: 'FinanceInvoices',
  mixins: [ InvoiceFormModal ],
  components: {
    InvoicesLinks,
    TableForPage,
    FinanceInvoicesModal,
    Filters,
    LabelBankCard,
    LabelTableStatus,
    LabelInvoiceType,
    PageHeader,
    InvoicesBulkCreation
  },
  data () {
    return {
      permissions: permissions,
      isShowFilter: false,
      isShowBulkCreation: false,
      modalLoading: false,
      filters: {
        'invoice_number': {
          type: 'input',
          name: 'invoice_number',
          label: 'Invoice number',
          placeholder: 'Invoice number'
        },
        'project': {
          type: 'select',
          name: 'project_id',
          label: 'Project',
          selectLabel: 'label',
          valueField: 'id',
          options: []
        },
        'date_from': {
          type: 'datepicker',
          name: 'date_from',
          label: 'Date from',
          format: 'dd/MM/yyyy',
          value: Moment().startOf('month').toISOString()
        },
        'date_to': {
          type: 'datepicker',
          name: 'date_to',
          label: 'Date to',
          format: 'dd/MM/yyyy',
          value: Moment().endOf('month').toISOString()
        },
        'bank_details': {
          type: 'multiselect',
          name: 'own_bank_details_id',
          nameItem: 'own_bank_details_id',
          label: 'Own bank details',
          selectLabel: 'summary',
          valueField: 'id',
          options: [],
          value: []
        },
        'client_details': {
          type: 'multiselect',
          name: 'client_bank_details_id',
          nameItem: 'client_bank_details_id',
          label: 'Client bank details',
          selectLabel: 'summary',
          valueField: 'id',
          options: [],
          value: []
        },
        'invoice_type': {
          type: 'select',
          name: 'invoice_type_id',
          label: 'Invoice type',
          selectLabel: 'type',
          valueField: 'id',
          options: []
        },
        'invoice_status': {
          type: 'select',
          name: 'invoice_status_id',
          label: 'Invoice status',
          selectLabel: 'display_name',
          valueField: 'id',
          options: []
        }
      },
      table: {
        slots: {
          invoice_number: {
            name: 'invoice_number'
          },
          own_bank_details: {
            name: 'own_bank_details'
          },
          client_bank_details: {
            name: 'client_bank_details'
          },
          invoice_type: {
            name: 'invoice_type'
          },
          invoice_status: {
            name: 'invoice_status'
          },
          total_amount: {
            name: 'total_amount'
          },
          files: {
            name: 'files'
          }
        },
        fields: [
          {
            key: 'invoice_number',
            label: 'Number',
            sortable: true,
            class: 'column-bd-name'
          },
          {
            key: 'project.name',
            label: 'Project name',
            class: 'column-bd-name'
          },
          {
            key: 'own_bank_details',
            label: 'Own bank details',
            class: 'column-type text-center'
          },
          {
            key: 'client_bank_details',
            label: 'Client bank details',
            class: 'column-type text-center'
          },
          {
            key: 'invoice_type',
            label: 'Type',
            class: 'column-icon text-center'
          },
          {
            key: 'invoice_status',
            label: 'Status',
            class: 'column-icon text-center'
          },
          {
            key: 'issue_date',
            label: 'Issue date',
            sortable: true,
            class: 'column-amount'
          },
          {
            key: 'due_date',
            label: 'Due date',
            sortable: true,
            class: 'column-amount'
          },
          {
            key: 'paid_date',
            label: 'Paid date',
            sortable: true,
            class: 'column-amount'
          },
          {
            key: 'total_amount',
            label: 'Amount',
            class: 'column-number'
          },
          {
            key: 'files',
            label: 'Files',
            class: 'column-icon text-center'
          },
          {
            key: 'actions',
            label: 'Actions',
            class: 'column-more'
          }
        ],
        actions: [
          { label: 'Delete', event: 'remove', show: this.$can(permissions.DeleteFinancesInvoices) }
        ],
        params: {
          filters: null,
          sorting: null,
          currentPage: '?page=1'
        }
      },
      tableRowModal: {
        fields: [
          {
            key: 'own_bank_details.summary',
            label: 'Own Bank Details'
          },
          {
            key: 'project.name',
            label: 'Project Name'
          },
          {
            key: 'client_bank_details.summary',
            label: 'Client Bank Details'
          },
          {
            key: 'invoice_status.display_name',
            label: 'Invoice Status'
          },
          {
            key: 'invoice_template_type.name',
            label: 'Invoice Template Type'
          },
          {
            key: 'comment',
            label: 'Comment'
          },
          {
            key: 'comment_national',
            label: 'Comment national'
          },
          {
            key: 'issue_date',
            label: 'Issue Date'
          },
          {
            key: 'due_date',
            label: 'Due Date'
          },
          {
            key: 'invoice_type.type',
            label: 'Invoice Type'
          },
          {
            key: 'client_bank_details',
            type: 'slot',
            label: 'Client name',
            slotName: 'client_name'
          },
          {
            key: 'total_amount',
            label: 'Total Amount'
          },
          {
            key: 'periods',
            type: 'slot',
            label: 'Periods',
            slotName: 'periods'
          },
          {
            key: 'items',
            type: 'slot',
            label: 'Items',
            slotName: 'items'
          },
          {
            key: 'specialisations',
            type: 'slot',
            label: 'Specialisations',
            slotName: 'specialisations'
          }
        ]
      },
      isDataPageLoading: true
    }
  },
  created () {
    this.isShowFilter = false
    this.fetchInvoiceFormData()
      .then(() => {
        this.setAllOptionsFilter()
        this.setFilters(this.filters)
      })
      .finally(() => {
        this.isShowFilter = true
      })
  },
  computed: {
    getClientsBankDetailsTableData () {
      if (!this.getInvoices.data) return []

      return this.getInvoices.data.map(item => {
        return {
          ...item,
          due_date: this._toLocalTime(item.due_date),
          paid_date: this._toLocalTime(item.paid_date),
          issue_date: this._toLocalTime(item.issue_date)
        }
      })
    },
    totalAmount () {
      return this.getInvoices.totalAmount
    }
  },
  methods: {
    toggleShowFilter () {
      this.isShowFilter = !this.isShowFilter
    },
    getFullName (data) {
      if (data) {
        return `${data.first_name} ${data.last_name}`
      }
    },
    getCurrencyCode (data) {
      if (data.client_bank_details) {
        return data.client_bank_details.currency.code
      }
      if (data.own_bank_details) {
        return data.own_bank_details.currency.code
      }
    },
    showViewModal (data) {
      this.$refs['table'].showView(data, `Invoice number ${data.item.invoice_number}`)
    },
    showCreateInvoices () {
      this.createInvoices.show = true
      this.createInvoices.form = null
    },
    hideInvoices () {
      this.createInvoices.show = false
    },
    async setFilters (data) {
      await this.setParamsFilters(data)
      await this.setParamsCurrentPage()
      this.fetchInvoices()
    },
    setParamsFilters (data) {
      const that = this
      const filters = Object.values(data).reduce(function (state, current) {
        let value
        if (current['value']) {
          if (current['type'] === 'datepicker') {
            value = that._toLocalTime(current['value'])
          } else if (Array.isArray(current['value'])) {
            value = JSON.stringify(current['value'].map(item => {
              return item.id
            }))
          } else {
            value = typeof current['value'] === 'object'
              ? current['value'][current['valueField']] : current['value']
          }

          if (value) {
            state += `&filter[${current.name}]=${value}`
          }
        }
        return state
      }, '')

      this.table.params.filters = filters || null
    },
    setParamsSort (sortDesc, sortBy) {
      if (sortBy) {
        const sortDirection = sortDesc ? 'desc' : 'asc'
        this.table.params.sorting = `&sort[${sortBy}]=${sortDirection}`
      }
    },
    setParamsCurrentPage (currentPage = 1) {
      this.table.params.currentPage = `?page=${currentPage}`
    },
    updateInvoices (data) {
      if (!data) return this.fetchInvoices()

      const { currentPage, sortDesc, sortBy } = data

      this.setParamsCurrentPage(currentPage)
      this.setParamsSort(sortDesc, sortBy)

      this.fetchInvoices()
    },
    setAllOptionsFilter () {
      this.filters.bank_details.options = this.getOwnBankDetails
      this.filters.client_details.options = this.getClientsBankDetailsAll
      this.filters.invoice_status.options = this.getStatuses
      this.filters.project.options = this.getProjects
      this.filters.invoice_type.options = this.getInvoicesTypes
    },
    fetchInvoices () {
      let params = this.table.params.currentPage + '&pagination=1'

      if (this.table.params.sorting) {
        params += this.table.params.sorting
      }
      if (this.table.params.filters) {
        params += this.table.params.filters
      }

      this._toggleDataPageLoading(() => this.$api.finances.getInvoices(params))
    },
    async removeInvoices (item) {
      const confirm = await this.$root.$confirm.open(
        'Delete',
        'Are you sure you want to delete the invoice?'
      )

      if (!confirm) return

      this._toggleDataPageLoading(async () => {
        await this.$api.finances.removeInvoices(item.id)
        this.updateInvoices()
      })
    },
    async createFinanceInvoices (fields) {
      this.createInvoice(fields)
        .then(() => {
          this.hideInvoices()
          this.updateInvoices()
        })
    }
  }
}
</script>

<style lang="scss">
  .entities-table-row-modal__period,
  .entities-table-row-modal__items{
    padding: 10px;
    margin-bottom: 15px;
    background-color: #eaeaea;
    &:last-child{
      margin-bottom: 0;
    }
  }
  .total-block {
    max-width: 400px;
  }
</style>
